import React, { useState } from 'react';
import { useQuery } from '@apollo/client';
import { isEmpty } from '@otp/utils';
import {
  Actions,
  AppArea,
  Subjects,
  WttpTemplateListFilterInput,
  WttpTemplatesDocument,
  WttpTemplatesQuery,
  WttpTemplateStatus,
} from 'src/graphql/generated/graphql';
import { WttpTemplatesFilter } from './filter';
import { WttpTemplatesTable } from './table';
import { useTable } from 'src/hooks/use-table';
import { Callout, Chip, Tabs, Typography } from '@otp/ui';
import { Trans, useTranslation } from 'react-i18next';
import { WttpTemplateAction } from './table/columns/actions/actions-modals';
import { useAuthContext } from 'src/context/auth.context';
import { CreateTemplateButton } from './create-template-button';
import { FilterWrapper } from 'src/components/common/wrapper/filter/wrapper';
import { ContentWrapper } from 'src/components/common/wrapper/content/wrapper';
import { TableWrapper } from 'src/components/common/wrapper/table/wrapper';
import { Link } from 'react-router-dom';
import { Routes } from 'src/components/common/router/constants';
import clsx from 'clsx';
import { Can } from 'src/utils/can';


const PAGINATION_LIMIT = 20;

enum TabType {
  Custom = 'custom',
  Global = 'global',
}

export const WttpTemplateList = () => {
  const { t } = useTranslation();
  const { isAdmin } = useAuthContext();

  const TABS = [
    { label: t('common.custom'), value: TabType.Custom },
    { label: t('common.global'), value: TabType.Global },
  ];

  const {
    page,
    setPage,
    filter: filterData,
    onFilterChange,
    defaultFilter,
  } = useTable<
    WttpTemplateListFilterInput & { status?: WttpTemplateStatus & 'archived' },
    Record<any, any>
  >();

  const { status: filterStatus, ...filter } = filterData || {};
  const isArchivedFilter = filterStatus === 'archived';

  const [activeTab, setActiveTab] = useState(
    isAdmin ? TabType.Global : TabType.Custom,
  );
  const [totalCount, setTotalCount] = useState(0);

  const { loading, data } = useQuery<WttpTemplatesQuery>(
    WttpTemplatesDocument,
    {
      variables: {
        filter: {
          ...filter,
          organizationId: activeTab === TabType.Custom ? undefined : null,
          isActive: !isArchivedFilter,
          includeDefaultOrganizationTemplate: isEmpty(filterData, {
            checkNestedKeys: true,
          }),
          statuses: filterStatus
            ? [filterStatus]
            : [WttpTemplateStatus.Draft, WttpTemplateStatus.Active],
        },
        sorting: {},
        pagination: {
          limit: PAGINATION_LIMIT,
          offset: page * PAGINATION_LIMIT,
        },
      },
      onCompleted: (data) =>
        setTotalCount(data?.wttpTemplates?.pagination?.totalCount),
      fetchPolicy: 'cache-and-network',
      nextFetchPolicy: 'cache-and-network',
    },
  );
  const wttpTemplates = data?.wttpTemplates?.records || [];

  const onActionFinished = (action: WttpTemplateAction) => {
    if (!isAdmin && action === WttpTemplateAction.SetDefault) {
      setActiveTab(TabType.Custom);
    }
  };

  const calloutColor = 'info';
  const callout = (
    <Callout icon={false} className="mb-4" severity={calloutColor}>
      <Typography variant="subtitle1" className={clsx('font-bold', 'mb-1')}>
        {t('wttp.template.list.callout.title')}
      </Typography>
      <Typography variant="subtitle2" className="mb-1">
        <div className={clsx('inline')}>
          <Chip label="1" color={calloutColor} />
          <Trans
            t={t}
            i18nKey="wttp.template.list.callout.step-1"
            values={{
              questionLink: t('wttp.template.list.callout.question-link'),
            }}
            components={{
              Link: (
                <Link
                  className={clsx('font-bold', 'underline')}
                  to={Routes.wttpQuestions.path}
                />
              ),
            }}
          />
        </div>
      </Typography>
      <Typography variant="subtitle2" className="mb-1">
        <div className={clsx('inline')}>
          <Chip label="2" color={calloutColor} />
          <Trans
            t={t}
            i18nKey="wttp.template.list.callout.step-2"
            values={{
              questionnaireLink: t(
                'wttp.template.list.callout.questionnaire-link',
              ),
            }}
            components={{
              Link: (
                <Link
                  className={clsx('font-bold', 'underline')}
                  to={Routes.wttpQuestionnaires.path}
                />
              ),
            }}
          />
        </div>
      </Typography>

      <Typography variant="subtitle2">
        <div className={clsx('inline')}>
          <Chip label="3" color={calloutColor} />
          {t('wttp.template.list.callout.step-3')}
        </div>
      </Typography>
    </Callout>
  );

  return (
    <Can I={Actions.Read} a={Subjects.Templates} appArea={AppArea.Wttp}>
      <FilterWrapper>
        <WttpTemplatesFilter
          onSubmit={onFilterChange}
          defaultFilter={defaultFilter}
        />
      </FilterWrapper>

      <ContentWrapper>
        <TableWrapper>
          {callout}

          <div className="relative h-12">
            {!isAdmin && (
              <Tabs
                tabs={TABS}
                onChange={(_, value) => setActiveTab(value)}
                value={activeTab}
              />
            )}
            <CreateTemplateButton />
          </div>

          <WttpTemplatesTable
            data={wttpTemplates}
            className="mt-1"
            loading={loading}
            options={{
              page,
              count: totalCount,
              sort: true,
              rowsPerPage: PAGINATION_LIMIT,
              onChangePage: setPage,
            }}
            onActionFinished={onActionFinished}
            isGlobalTemplatesOnly={activeTab === TabType.Global}
          />
        </TableWrapper>
      </ContentWrapper>
    </Can>
  );
};
